import React from "react";
import type { TabBarProps } from "./types";

/**
 * TabBar Component
 *
 * Displays horizontally scrollable tabs for filtering content feeds.
 * Active tab is indicated by a bold font weight and 3px underline.
 * Inactive tabs use regular font weight.
 *
 * Design specs (from Figma):
 * - Container height: 53px
 * - Font: Aktiv Grotesk (18px, size-18)
 * - Active: Bold (700 weight)
 * - Inactive: Regular (400 weight)
 * - Active indicator: 3px bottom border
 * - Padding: px-[16px] py-[11px]
 * - Gap: 11px between tabs
 * - Color: brand-neutral (#020231)
 */
export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onTabChange,
}) => {
  return (
    <div
      className="no-scrollbar relative h-[53px] w-full overflow-x-auto overflow-y-hidden"
      role="tablist"
      aria-label="Article feed tabs"
    >
      <div className="mx-auto flex h-[51px] w-max min-w-max items-center justify-center gap-[11px] lg:w-full mdDesktop:w-full">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={`relative flex h-[51px] shrink-0 items-center justify-center px-[16px] py-[11px] text-center transition-colors duration-200 ${
                isActive
                  ? "cursor-default font-bold text-brand-neutral font-emphasis-18"
                  : "cursor-pointer text-[18px] font-normal text-brand-neutral hover:text-brand-neutral"
              }`}
            >
              <span className="whitespace-nowrap lg:truncate mdDesktop:truncate">
                {tab.label}
              </span>

              {isActive && (
                <div
                  className="absolute -bottom-px left-0 right-0 h-[3px] bg-brand-neutral"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-[1px] bg-brand-neutral"
        aria-hidden
      />
    </div>
  );
};

export default TabBar;
